/*
 * ttg-foto.js — Foto-Schritt der Foto-Fußmatte (Schema v1.18).
 *
 * EIGENSTÄNDIGES Modul neben der Engine (CLAUDE.md §3: Feature-Code in eigene
 * Assets). Die Engine rendert nur den Mount-Punkt ([data-ttg="photo-step"]) und
 * ruft window.TTG_FOTO.mountStep(engine, el) — dieselbe Naht-Idee wie
 * TTG_SIZE_OVERLAY_RENDERERS. Ohne dieses Modul bleibt der Schritt ein Hinweis
 * und der Kauf sauber blockiert (engine._photoSatisfied() bleibt false).
 *
 * Verantwortung hier:
 *  - Dateiwahl (JPG/PNG; HEIC nur, wo der Browser es dekodiert — Safari),
 *    EXIF-Lage über createImageBitmap(imageOrientation:'from-image').
 *  - Clientseitiges Verkleinern auf max. TTG_FOTO_MAX_EDGE px (JPEG q0.9) —
 *    diese Pixel sind zugleich die DRUCK-Quelle: state.photo.crop ist in den
 *    Pixeln des verkleinerten Bildes, und genau diese Datei geht zum Server.
 *  - Verschieben (Pointer-Drag, ein Finger) und Zoomen (Slider + Pinch) direkt
 *    auf der Bühne (engine.refs.photo); der Rahmen ist der Zuschnitt.
 *  - „Automatisch ausrichten" = cover, mittig (Zoom 1).
 *  - Auflösungs-Ampel auf den SICHTBAREN Ausschnitt gerechnet (crop-Kante gegen
 *    config.photo.warnMinEdgePx je Größen-Optionswert) — warnt, blockiert nie.
 *  - Upload an config.photo.uploadUrl (roher JPEG-Body, Antwort {url}); erst
 *    mit Server-URL ist der Schritt erfüllt (fail-closed bei Upload-Fehler,
 *    mit Retry-Knopf).
 *
 * Der Modul-Zustand je Engine lebt an engine._foto (Blob, Objekt-URL, Zoom) —
 * renderSteps() baut die Karte beliebig oft neu, mountStep ist idempotent.
 */
(function () {
  'use strict';

  var MAX_EDGE = 4000;          // Kantenmaß nach Downscale (deckt 300 dpi bei 50×75: 3248 px)
  var UPLOAD_MAX_BYTES = 12 * 1024 * 1024;
  var JPEG_QUALITY = 0.9;
  var ZOOM_MAX = 4;
  var ZOOM_MIN_CUT = 0.55;      // nur bei freigestelltem Foto, s. zoomMin()
  // Standzeit des Buehnen-Hinweises (zeigeStageHint). REIN zeitgesteuert,
  // bewusst nicht an Scroll gekoppelt — die Motivbuch-Hinweise starben an
  // einem Scroll-Schliesser nach ~1,5 s, bevor sie jemand lesen konnte.
  var STAGE_HINT_MS = 4000;

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  /** Rahmen-Seitenverhältnis (B/H) aus der photo-Layer-Box — Bühne ist quadratisch,
   *  Prozent gegen Prozent ist deshalb das echte Verhältnis. */
  function frameAspect(engine) {
    var l = engine._photoLayer && engine._photoLayer();
    if (!l || !l.heightPercent) return 1;
    return (l.widthPercent || 1) / l.heightPercent;
  }

  /** Cover-Zuschnitt (größtes Rechteck im Rahmen-Verhältnis), zentriert, geteilt durch Zoom. */
  function coverCrop(srcW, srcH, aspect, zoom, cx, cy) {
    var w = Math.min(srcW, srcH * aspect);
    var h = w / aspect;
    w = w / zoom; h = h / zoom;
    var x = (cx != null ? cx : srcW / 2) - w / 2;
    var y = (cy != null ? cy : srcH / 2) - h / 2;
    // Klemmen nur, solange der Ausschnitt IN die Quelle passt. Ist er groesser
    // (Zoom unter 1, nur bei freigestelltem Foto moeglich), wird stattdessen
    // zentriert — `Math.max(0, Math.min(srcW - w, x))` zoege x sonst auf 0,
    // weil `srcW - w` dann negativ ist, und der Hund saesse in der Ecke statt
    // in der Mitte. Genau diese Zeile war die stille Falle an der neuen
    // Untergrenze (30.08.2026).
    x = (w >= srcW) ? (srcW - w) / 2 : Math.max(0, Math.min(srcW - w, x));
    y = (h >= srcH) ? (srcH - h) / 2 : Math.max(0, Math.min(srcH - h, y));
    return { x: x, y: y, w: w, h: h };
  }

  function fotoState(engine) {
    if (!engine._foto) engine._foto = { zoom: 1, uploading: false, error: null, blob: null, uebernahme: false };
    return engine._foto;
  }

  /** Warnschwelle für die aktuell gewählte Größe; ohne Wahl die kleinste Schwelle
   *  (nicht nörgeln, bevor die Größe feststeht — refresh() zieht nach). */
  function warnEdge(engine) {
    var map = (engine.config.photo && engine.config.photo.warnMinEdgePx) || {};
    var size = engine.state.size;
    if (size && map[size] != null) return Number(map[size]) || 0;
    var min = 0;
    for (var k in map) {
      var v = Number(map[k]) || 0;
      if (v && (!min || v < min)) min = v;
    }
    return min;
  }

  function updateWarn(engine) {
    var ui = engine._foto && engine._foto.ui;
    if (!ui || !ui.warn) return;
    var p = engine.state.photo;
    var S = engine.config.strings || {};
    if (!p || !p.crop) { ui.warn.hidden = true; return; }
    // Deckungsfaktor (29.08.2026): crop ist ein rein geometrisches Rechteck und
    // kennt kein Alpha. Bei einem freigestellten Foto steht in diesem Rechteck
    // deutlich weniger Bildinformation, als seine Kante verspricht — ohne den
    // Faktor meldete die Ampel „genug Auflösung", wo keine ist. Ohne Alpha ist
    // p.deckung 1 und die Rechnung exakt die bisherige (s. messeDeckung).
    var deckung = (typeof p.deckung === 'number' && p.deckung > 0) ? p.deckung : 1;
    var edge = Math.min(p.crop.w, p.crop.h) * deckung;
    var need = warnEdge(engine);
    if (need && edge < need) {
      var msg = S.photo_low_res
        || 'Dein Foto hat für diese Größe wenig Auflösung ({px} px im Ausschnitt, empfohlen {need} px). Es kann etwas unscharf gedruckt werden — näher heranzoomen verringert die Schärfe weiter.';
      ui.warn.textContent = msg.replace('{px}', String(Math.round(edge))).replace('{need}', String(need));
      ui.warn.hidden = false;
    } else {
      ui.warn.hidden = true;
    }
  }

  function updateStatus(engine) {
    var f = fotoState(engine);
    var ui = f.ui;
    if (!ui || !ui.status) return;
    var S = engine.config.strings || {};
    var p = engine.state.photo;
    ui.retry.hidden = true;
    if (f.error) {
      ui.status.textContent = f.error;
      ui.status.className = 'ttg-photo__status ttg-photo__status--err';
      ui.retry.hidden = !f.blob;              // Retry nur, wenn es etwas hochzuladen gibt
    } else if (f.uploading) {
      ui.status.textContent = S.photo_uploading || 'Foto wird hochgeladen …';
      ui.status.className = 'ttg-photo__status';
    } else if (f.uebernahme) {
      // Der Upload ist durch, aber das Server-Bild wird noch geholt und
      // dekodiert (beim Freischnitt 6-8 s). Ohne diesen Zweig meldete die Zeile
      // bereits „Foto gespeichert", waehrend ueber der Buehne noch der Ring
      // stand — und der Kunde las die Fertigmeldung (Jens, 31.08.2026).
      // Derselbe Text wie der Ring, aus DERSELBEN Quelle (ladeText), damit die
      // beiden nicht wieder auseinanderlaufen koennen.
      ui.status.textContent = ladeText(engine);
      ui.status.className = 'ttg-photo__status';
    } else if (p && p.url) {
      ui.status.textContent = S.photo_ready || 'Foto gespeichert. Du kannst es verschieben und zoomen.';
      ui.status.className = 'ttg-photo__status ttg-photo__status--ok';
    } else {
      ui.status.textContent = '';
      ui.status.className = 'ttg-photo__status';
    }
    var has = !!(p && p.crop);
    if (ui.tools) ui.tools.hidden = !has;
    if (ui.pick) ui.pick.textContent = has
      ? (S.photo_change || 'Anderes Foto wählen')
      : (S.photo_pick || 'Foto auswählen');
  }

  /** Datei → verkleinertes JPEG (Blob) + Maße. EXIF-Lage angewandt. */
  function decodeAndScale(file) {
    var viaBitmap = (typeof createImageBitmap === 'function')
      ? createImageBitmap(file, { imageOrientation: 'from-image' }).catch(function () { return null; })
      : Promise.resolve(null);
    return viaBitmap.then(function (bmp) {
      if (bmp) return bmp;
      // Fallback (ältere Browser): <img> über Objekt-URL — Browser wendet EXIF an.
      return new Promise(function (resolve, reject) {
        var url = URL.createObjectURL(file);
        var img = new Image();
        img.onload = function () { resolve(img); };
        img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode')); };
        img.src = url;
      });
    }).then(function (src) {
      var w = src.width || src.naturalWidth;
      var h = src.height || src.naturalHeight;
      if (!w || !h) throw new Error('decode');
      var s = Math.min(1, MAX_EDGE / Math.max(w, h));
      var cw = Math.max(1, Math.round(w * s));
      var ch = Math.max(1, Math.round(h * s));
      var canvas = document.createElement('canvas');
      canvas.width = cw; canvas.height = ch;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(src, 0, 0, cw, ch);
      if (src.close) try { src.close(); } catch (e) { /* egal */ }
      return new Promise(function (resolve, reject) {
        var enc = function (q) {
          canvas.toBlob(function (blob) {
            if (!blob) return reject(new Error('encode'));
            // Größenschutz: über dem Limit einmal gröber nachkodieren.
            if (blob.size > UPLOAD_MAX_BYTES && q > 0.75) return enc(0.75);
            resolve({ blob: blob, w: cw, h: ch });
          }, 'image/jpeg', q);
        };
        enc(JPEG_QUALITY);
      });
    });
  }

  /**
   * Server-Bild übernehmen, ohne dass die Bühne flackert.
   *
   * Ablauf, absichtlich in dieser Reihenfolge:
   *   1. Ladehinweis ÜBER das weiter sichtbare Original legen (kein Leerrahmen).
   *   2. Server-Datei im Hintergrund laden — erst `decode()`, damit sie beim
   *      Einsetzen fertig dekodiert ist und kein halbes Bild aufblitzt.
   *   3. localUrl freigeben und auf null setzen; ab jetzt nehmen Vorschau,
   *      Snapshot und Druckvertrag dieselbe Quelle.
   *   4. Deckungsfaktor messen (s. messeDeckung) und Ampel nachziehen.
   * Schlägt das Laden fehl, bleibt localUrl stehen — dann zeigt die Bühne
   * weiter das Original statt gar nichts. `p.url` ist trotzdem gesetzt, der
   * Druck bekommt also die richtige Datei.
   */
  function uebernehmeServerbild(engine, url) {
    var f = fotoState(engine);
    var p = engine.state.photo;
    if (!p) return;
    if (!f.localUrl) { messeDeckung(engine, url); return; }   // nichts abzulösen

    // Ein ZUSTAND, nicht nur ein DOM-Knoten: die Statuszeile muss dieselbe
    // Frage beantworten koennen, und nach einem renderSteps() baut mountStep
    // sie aus genau diesem Zustand neu auf.
    f.uebernahme = true;
    zeigeLadehinweis(engine, true);
    updateStatus(engine);
    var img = new Image();
    img.crossOrigin = 'anonymous';
    var fertig = function () {
      f.uebernahme = false;
      zeigeLadehinweis(engine, false);
      if (f.localUrl) {
        try { URL.revokeObjectURL(f.localUrl); } catch (e) { /* egal */ }
        f.localUrl = null;
      }
      if (engine.state.photo) engine.state.photo.localUrl = null;
      engine.renderPreview();
      messeDeckung(engine, url);
      updateStatus(engine);          // erst JETZT darf „Foto gespeichert" stehen
    };
    img.onload = function () {
      if (img.decode) img.decode().then(fertig, fertig); else fertig();
    };
    img.onerror = function () {
      // Bewusst KEIN Ablösen: lieber weiter das Original zeigen als eine
      // leere Bühne. Der Druck ist davon unberührt (p.url steht).
      // Das Flag MUSS auch hier fallen — sonst haenge die Zeile dauerhaft auf
      // „einen Moment", und der Schritt saehe unfertig aus, obwohl er es nicht ist.
      f.uebernahme = false;
      zeigeLadehinweis(engine, false);
      updateStatus(engine);
    };
    img.src = url;
  }

  /** Ladehinweis über der Bühne — dasselbe Overlay-Muster wie der Stage-Hint.
   *
   * Der Text hängt daran, ob das Produkt FREISTELLT (30.08.2026). Grund: diese
   * Funktion läuft für ALLE vier Foto-Matten — `uebernehmeServerbild` wird nach
   * jedem Upload aufgerufen, nicht nur bei Design 4. Ein Text wie „Wir
   * schneiden Deinen Hund frei" stünde sonst auch auf den drei Bestandsmatten,
   * die das Foto ins Rechteck setzen und gar nichts freistellen.
   *
   * Die Wartezeit ist der Grund für die auffälligere Gestaltung: mit
   * Freistellen dauert ein Bild in der Produktion 6–8 s, ohne ist es nach
   * Bruchteilen einer Sekunde vorbei. Das Verhalten ist unverändert — der
   * Hinweis erscheint bei JEDEM Bild neu und verschwindet in `fertig()` wie in
   * `img.onerror`, bleibt also auch bei einem Fehlschlag nicht stehen.
   */
  /** Der Wartetext — EINE Quelle fuer Ring und Statuszeile (31.08.2026).
   *  Standen sie getrennt, liefen sie auseinander: der Ring sagte „einen
   *  Moment", die Zeile daneben „Foto gespeichert". */
  function ladeText(engine) {
    var S = engine.config.strings || {};
    return (engine.config.photo || {}).freistellen === true
      ? (S.photo_busy_cut || 'Wir schneiden Deinen Hund frei … einen Moment.')
      : (S.photo_busy || 'Foto wird vorbereitet …');
  }

  function zeigeLadehinweis(engine, an) {
    var layer = engine.refs && engine.refs.photo;
    if (!layer) return;
    var vorhanden = layer.querySelector('.ttg__photo-busy');
    if (!an) { if (vorhanden) vorhanden.remove(); return; }
    if (vorhanden) return;
    var text = ladeText(engine);
    var box = el('span', 'ttg__photo-busy');
    // role=status meldet den Wechsel auch dort, wo niemand hinsieht — die
    // Anzeige ist sonst rein visuell.
    box.setAttribute('role', 'status');
    // Das Symbol traegt keine Bedeutung, nur die Bewegung. Fuer die Vorlesehilfe
    // ist es deshalb ausgeblendet, sonst laese sie einen leeren Knoten vor.
    var ring = el('span', 'ttg__photo-busy-ring');
    ring.setAttribute('aria-hidden', 'true');
    box.appendChild(ring);
    box.appendChild(el('span', 'ttg__photo-busy-text', text));
    layer.appendChild(box);
  }

  /**
   * Deckungsfaktor des Server-Bildes messen — EINMALIG nach dem Upload.
   *
   * Warum: `warnMinEdgePx` vergleicht gegen `min(crop.w, crop.h)`, und crop ist
   * ein rein geometrisches Rechteck. Bei einem freigestellten Foto ist der
   * größere Teil davon transparent — gemessen am Testbild 55,7 % — und die
   * Ampel meldete 1141 px, wo nur 902 px echte Bildinformation stehen: 20,9 %
   * zu optimistisch.
   *
   * Der Faktor ist eine Eigenschaft der DATEI und ändert sich beim Zoomen oder
   * Verschieben nicht — deshalb einmal messen und merken, statt bei jedem
   * crop-Wechsel zu rechnen (updateWarn bleibt damit O(1)).
   *
   * 4-px-Raster statt jedem Pixel: gemessen 24 ms statt 330 ms bei 1,46 MP,
   * Abweichung der Kante 0,5 % (897 gegen 902 px). Ein Vollscan läge bei
   * 4000 px hochgerechnet bei rund 3,8 s.
   *
   * Ohne Alpha (jedes JPEG) ist der Faktor exakt 1 — dann verhält sich die
   * Ampel byte-identisch zu vorher, und die drei Bestandsmatten sind unberührt.
   */
  function messeDeckung(engine, url) {
    var p = engine.state.photo;
    if (!p) return;
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        var w = img.naturalWidth, h = img.naturalHeight;
        if (!w || !h) return;
        var cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        var ctx = cv.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        var d = ctx.getImageData(0, 0, w, h).data;
        var minX = w, minY = h, maxX = -1, maxY = -1;
        for (var y = 0; y < h; y += 4) {
          var reihe = y * w;
          for (var x = 0; x < w; x += 4) {
            if (d[(reihe + x) * 4 + 3] > 8) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX < 0) return;                       // vollständig transparent — nicht werten
        var iw = maxX - minX + 1, ih = maxY - minY + 1;
        var faktor = Math.min(iw, ih) / Math.min(w, h);
        // Nur ein ECHTER Freischnitt senkt den Faktor. Rundungsrauschen des
        // Rasters (bis ~1 %) darf die Ampel nicht verschieben.
        p.deckung = (faktor > 0 && faktor < 0.99) ? faktor : 1;
        updateWarn(engine);
      } catch (e) {
        // Canvas verweigert (fremde Herkunft ohne CORS): Faktor bleibt 1, die
        // Ampel rechnet wie bisher. Kein Grund, den Kauf aufzuhalten.
        try { console.warn('[ttg-foto] Deckung nicht messbar:', e && e.message); } catch (e2) { /* egal */ }
      }
    };
    img.src = url;
  }

  function upload(engine) {
    var f = fotoState(engine);
    var cfg = engine.config.photo || {};
    if (!f.blob || !cfg.uploadUrl) return;
    f.uploading = true; f.error = null;
    updateStatus(engine);
    // Freistellen (Schema v1.22, Design 4): NUR wenn die Config es am
    // photo-Schritt anfordert. Die drei Bestands-Foto-Matten setzen das Foto
    // ins Rechteck und tragen das Feld nicht — für sie bleibt die Adresse
    // unverändert, der Aufruf byte-gleich zu vorher.
    var ziel = cfg.uploadUrl;
    if (cfg.freistellen === true) {
      ziel += (ziel.indexOf('?') >= 0 ? '&' : '?') + 'freistellen=1';
    }
    fetch(ziel, {
      method: 'POST',
      headers: { 'Content-Type': 'image/jpeg' },
      body: f.blob,
    }).then(function (res) {
      if (!res.ok) {
        // Der Freischnitt scheitert mit 502 und nennt den Grund. Ihn hier
        // gesondert zu fassen ist der Unterschied zwischen „Das Hochladen hat
        // nicht geklappt" und einer Meldung, mit der der Kunde etwas anfangen
        // kann — hochgeladen hat es ja geklappt.
        if (res.status === 502) {
          return res.json().catch(function () { return {}; }).then(function (d) {
            var e = new Error('cutout');
            e.ttgCutout = true;
            e.ttgDetail = (d && d.detail) || '';
            throw e;
          });
        }
        throw new Error('http ' + res.status);
      }
      return res.json();
    }).then(function (data) {
      if (!data || !data.url) throw new Error('keine url');
      f.uploading = false;
      if (engine.state.photo) {
        engine.state.photo.url = data.url;
        engine.state.done.photo = true;
      }
      // localUrl abloesen (29.08.2026) — der Grund ist ein stiller
      // Vorschau-Druck-Bruch: Vorschau (_applyPhotoLayer) und Snapshot nehmen
      // `p.localUrl || p.url`, der DRUCKVERTRAG dagegen nur `p.url`. Solange
      // die lokale Objekt-URL steht, gewinnt sie in den ersten beiden Wegen.
      // Sobald der Server ein anderes Bild zurueckgibt als das hochgeladene —
      // genau der Fall beim serverseitigen Freistellen — saehe der Kunde sein
      // Original und gedruckt wuerde der Freischnitt.
      //
      // Der Wechsel laeuft ueber `uebernehmeServerbild`: Das Original bleibt
      // sichtbar, bis die Server-Datei WIRKLICH geladen ist; erst dann faellt
      // localUrl weg. Kein Leerrahmen, kein Umspringen auf ein halb geladenes
      // Bild — auch nicht bei 3 s Freistellzeit.
      uebernehmeServerbild(engine, data.url);
      updateStatus(engine);
      // Schrittliste + CTA nachziehen (der Schritt ist jetzt erfüllt).
      engine.renderSteps();
      if (typeof engine._updateCta === 'function') engine._updateCta();
    }).catch(function (e) {
      f.uploading = false;
      var S = engine.config.strings || {};
      // Zwei Fälle, zwei Meldungen. Beim Freischnitt IST das Hochladen
      // geglückt — „Bitte erneut versuchen" wäre hier ein falscher Rat, denn
      // dasselbe Foto scheitert wieder. Der Kunde soll ein anderes nehmen,
      // genau wie der Hinweis unter dem Feld es ankündigt.
      f.error = e && e.ttgCutout
        ? (S.photo_cutout_error
           || 'Bei diesem Foto hat der automatische Freischnitt nicht geklappt. '
              + 'Probier bitte ein anderes — am besten eins mit ruhigem Hintergrund.')
        : (S.photo_upload_error || 'Das Hochladen hat nicht geklappt. Bitte erneut versuchen.');
      try {
        console.warn('[ttg-foto] ' + (e && e.ttgCutout ? 'Freistellen' : 'Upload')
          + ' fehlgeschlagen:', (e && (e.ttgDetail || e.message)) || e);
      } catch (e2) { /* egal */ }
      updateStatus(engine);
      if (typeof engine._updateCta === 'function') engine._updateCta();
    });
  }

  function setCrop(engine, crop) {
    var p = engine.state.photo;
    if (!p) return;
    p.crop = crop;
    engine.renderPreview();
    updateWarn(engine);
  }

  /** Bedien-Hinweis DIREKT AM RAHMEN (Phase 7 — der Hinweis in der Step-Karte
   *  wurde uebersehen): erscheint mit dem ersten Foto, verschwindet bei der
   *  ersten Geste (Drag oder Zoom-Regler). Lebt im Foto-Layer der Buehne —
   *  renderPreview schreibt dort nur Styles, der Knoten ueberlebt Re-Renders. */
  function zeigeStageHint(engine) {
    var layer = engine.refs && engine.refs.photo;
    if (!layer || engine._fotoHintWeg || layer.querySelector('.ttg__photo-hint')) return;
    var S = engine.config.strings || {};
    var hint = el('span', 'ttg__photo-hint',
      S.photo_stage_hint || 'Zieh Dein Foto zurecht – zoomen per Regler');
    layer.appendChild(hint);
    (engine._fotoFlaechen || []).forEach(function (f) { if (!f.querySelector('.ttg__photo-hint')) f.appendChild(el('span', 'ttg__photo-hint', hint.textContent)); });
    // Auto-Hide (28.08.2026): Wer sein Foto nach dem Auto-Cover gut findet und
    // NICHTS anfasst, behielt den Balken bis zum Warenkorb — mitten im Motiv,
    // bei der kleinen Dosen-Fotoflaeche besonders stoerend. Nach STAGE_HINT_MS
    // raeumt er sich selbst; eine Geste vorher raeumt den Timer (s. unten).
    engine._fotoHintTimer = setTimeout(function () { versteckeStageHint(engine); }, STAGE_HINT_MS);
  }
  function versteckeStageHint(engine) {
    engine._fotoHintWeg = true;
    if (engine._fotoHintTimer) {
      clearTimeout(engine._fotoHintTimer);
      engine._fotoHintTimer = null;
    }
    var layer = engine.refs && engine.refs.photo;
    var hint = layer && layer.querySelector('.ttg__photo-hint');
    if (hint) hint.remove();
    (engine._fotoFlaechen || []).forEach(function (f) { var h = f.querySelector('.ttg__photo-hint'); if (h) h.remove(); });
  }

  function onFile(engine, file) {
    if (!file) return;
    var f = fotoState(engine);
    var S = engine.config.strings || {};
    f.error = null;
    updateStatus(engine);
    decodeAndScale(file).then(function (out) {
      if (f.localUrl) { try { URL.revokeObjectURL(f.localUrl); } catch (e) { /* egal */ } }
      f.blob = out.blob;
      f.localUrl = URL.createObjectURL(out.blob);
      f.zoom = 1;
      var crop = coverCrop(out.w, out.h, frameAspect(engine), 1);
      engine.state.photo = {
        url: null,
        localUrl: f.localUrl,
        srcW: out.w, srcH: out.h,
        crop: crop,
        name: file.name || '',
      };
      if (f.ui && f.ui.zoom) f.ui.zoom.value = '1';
      engine.state.done.photo = false;
      engine.renderPreview();
      engine.renderSteps();
      zeigeStageHint(engine);
      upload(engine);
    }).catch(function () {
      f.error = S.photo_decode_error
        || 'Dieses Bild kann Dein Browser nicht lesen (z. B. HEIC außerhalb von Safari). Bitte als JPG oder PNG exportieren.';
      updateStatus(engine);
    });
  }

  /**
   * Untergrenze des Zooms (30.08.2026).
   *
   * Ohne Freistellen ist 1 richtig: „Rahmen vollstaendig ausgefuellt". Ein Wert
   * darunter liesse bei den drei Rechteck-Matten den Hintergrund unter dem
   * Rechteck durchscheinen — genauer: der Druck bekaeme transparente Streifen,
   * wo der Kunde Foto erwartet, und das GATE lehnt so einen Vertrag ab
   * (render.py, ohne `foto.randUeberstand`).
   *
   * Beim Freischnitt gibt es keinen Hintergrund, der sichtbar werden koennte —
   * der Hund darf kleiner als der Rahmen sein, daneben bleibt Matte.
   *
   * 0,55 ist am Live-Layout eingemessen: Der Hund ist dort 98 mm breit in einem
   * 228,6-mm-Rahmen, also gut 40 % — deutlich Luft ringsum, und mit 258 dpi weit
   * ueber jeder Druckschwelle. Darunter wird er auf einer 40x60-Matte kleiner
   * als eine Handflaeche; das sieht nicht mehr nach Gestaltung aus, sondern nach
   * Versehen. Es ist eine GRENZE, kein Vorgabewert — der Kunde startet
   * weiterhin bei 1.
   */
  function zoomMin(engine) {
    return (engine.config.photo || {}).freistellen === true ? ZOOM_MIN_CUT : 1;
  }

  function applyZoom(engine, zoom) {
    var p = engine.state.photo;
    if (!p) return;
    versteckeStageHint(engine);
    var f = fotoState(engine);
    f.zoom = Math.max(zoomMin(engine), Math.min(ZOOM_MAX, zoom));
    var cx = p.crop.x + p.crop.w / 2;
    var cy = p.crop.y + p.crop.h / 2;
    setCrop(engine, coverCrop(p.srcW, p.srcH, frameAspect(engine), f.zoom, cx, cy));
  }

  /** Bühnen-Gesten (einmal je Engine): Drag = verschieben, Pinch = zoomen.
   *  touch-action:none kommt aus dem CSS (.ttg__layer--photo). */
  function bindStage(engine, flaeche) {
    // flaeche (Opt-in, 14.10.2026 Tassen): weitere Griff-Fläche mit denselben Gesten — ttg-tasse.js legt sie über
    // Herz/Oval auf der gezeichneten Tasse, weil die Foto-Ebene dort unter der Tassen-Vorschau liegt. Ohne flaeche
    // unverändert: die Foto-Ebene der Bühne, einmal je Engine.
    var layer;
    if (flaeche) {
      if (flaeche._ttgFotoBound) return;
      layer = flaeche; flaeche._ttgFotoBound = true;
      (engine._fotoFlaechen = engine._fotoFlaechen || []).push(flaeche);
      var h0 = engine.refs && engine.refs.photo && engine.refs.photo.querySelector('.ttg__photo-hint');
      if (h0 && !engine._fotoHintWeg) flaeche.appendChild(el('span', 'ttg__photo-hint', h0.textContent));
    } else {
      if (engine._fotoStageBound) return;
      layer = engine.refs && engine.refs.photo;
      if (!layer) return;
      engine._fotoStageBound = true;
    }
    var pointers = new Map();
    var start = null;

    layer.addEventListener('pointerdown', function (e) {
      if (!engine.state.photo) return;
      versteckeStageHint(engine);
      // Ein PRIMARY-Pointer beginnt per Spec eine frische Geste (alle Finger waren
      // oben) — liegengebliebene Pointer sind Geister (07.10.2026, Kissen-Briefing 12).
      // Anlass: Hatte ein anderes Element den Pointer per Capture an sich gezogen
      // (Mini-Vorschau), kam hier nie ein pointerup an; der alte Finger blieb in der
      // Map, und der nächste EIN-Finger-Wisch lief als Pinch und zoomte statt zu
      // verschieben (gemessen: Ausschnitt 1919 → 1537 px).
      if (e.isPrimary) { pointers.clear(); start = null; }
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try { layer.setPointerCapture(e.pointerId); } catch (e2) { /* egal */ }
      var p = engine.state.photo;
      start = {
        crop: { x: p.crop.x, y: p.crop.y, w: p.crop.w, h: p.crop.h },
        zoom: fotoState(engine).zoom,
        dist: null,
      };
      if (pointers.size === 2) {
        var pts = Array.from(pointers.values());
        start.dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      }
      e.preventDefault();
    });

    layer.addEventListener('pointermove', function (e) {
      if (!pointers.has(e.pointerId) || !start || !engine.state.photo) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      var p = engine.state.photo;
      var rect = layer.getBoundingClientRect();
      var pxPerSrc = rect.width / p.crop.w;   // Bildschirm-px je Quell-px
      if (pointers.size >= 2) {
        // Pinch: Abstand der zwei Finger → Zoomfaktor relativ zum Start.
        var pts = Array.from(pointers.values());
        var dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        if (start.dist == null) { start.dist = dist; return; }
        var z = Math.max(zoomMin(engine), Math.min(ZOOM_MAX, start.zoom * (dist / start.dist)));
        fotoState(engine).zoom = z;
        if (fotoState(engine).ui && fotoState(engine).ui.zoom) fotoState(engine).ui.zoom.value = String(z);
        var cx = start.crop.x + start.crop.w / 2;
        var cy = start.crop.y + start.crop.h / 2;
        setCrop(engine, coverCrop(p.srcW, p.srcH, frameAspect(engine), z, cx, cy));
      } else {
        // Drag: Fingerbewegung gegen die Quell-Pixel umrechnen (Foto folgt dem
        // Finger → der Ausschnitt wandert entgegen).
        var first = pointers.values().next().value;
        var dx = (first.x - (start.sx != null ? start.sx : first.x));
        if (start.sx == null) { start.sx = first.x; start.sy = first.y; return; }
        var dy = first.y - start.sy;
        var nx = start.crop.x - dx / pxPerSrc;
        var ny = start.crop.y - dy / pxPerSrc;
        nx = Math.max(0, Math.min(p.srcW - p.crop.w, nx));
        ny = Math.max(0, Math.min(p.srcH - p.crop.h, ny));
        setCrop(engine, { x: nx, y: ny, w: p.crop.w, h: p.crop.h });
      }
      e.preventDefault();
    });

    var endPointer = function (e) {
      pointers.delete(e.pointerId);
      if (!pointers.size) start = null;
      else {
        // Vom Pinch zurück zum Drag: Startpunkte neu setzen.
        var p = engine.state.photo;
        if (p) {
          start = { crop: { x: p.crop.x, y: p.crop.y, w: p.crop.w, h: p.crop.h }, zoom: fotoState(engine).zoom, dist: null };
        }
      }
    };
    layer.addEventListener('pointerup', endPointer);
    layer.addEventListener('pointercancel', endPointer);
  }

  var TTG_FOTO = {
    // Interne Testnaht (Unterstrich = kein oeffentlicher Vertrag): der
    // Buehnen-Hinweis ist sonst nur ueber onFile/Gesten erreichbar, und die
    // brauchen Bild-Dekodierung bzw. Pointer-Events, die es in Node nicht
    // gibt. Genutzt von test/foto-stage-hint.mjs.
    _zeigeStageHint: zeigeStageHint,
    _versteckeStageHint: versteckeStageHint,
    // Ebenfalls Testnaht (30.08.2026): die Zoom-Untergrenze und der Zuschnitt
    // sind sonst nur ueber Slider- und Pinch-Ereignisse erreichbar. Gerade der
    // Fall „Ausschnitt groesser als die Quelle" laesst sich so direkt pruefen,
    // statt ihn ueber eine Geste nachzustellen.
    _zoomMin: zoomMin,
    // Testnaht 07.10.2026: Bühnen-Gesten (Drag/Pinch) — sonst nur über echte
    // Pointer-Events am Rahmen erreichbar. Genutzt von test/foto-touch-verschieben.mjs.
    _bindStage: bindStage,
    /** Zusätzliche Griff-Fläche mit denselben Gesten wie die Foto-Ebene (Drag/Pinch, Opt-in; genutzt von ttg-tasse.js). */
    bindFlaeche: function (engine, flaeche) { bindStage(engine, flaeche); },
    _coverCrop: coverCrop,
    // Testnaht 31.08.2026: Statuszeile und Wartetext. Beide haengen an
    // engine._foto und sind sonst nur ueber einen echten Upload erreichbar.
    _updateStatus: updateStatus,
    _ladeText: ladeText,

    /** Vom Engine-renderSteps() gerufen — idempotent, baut die Karte auf. */
    mountStep: function (engine, mount) {
      if (!engine || !mount || mount.dataset.ttgFotoMounted) return;
      mount.dataset.ttgFotoMounted = '1';
      var S = engine.config.strings || {};
      var f = fotoState(engine);
      mount.textContent = '';

      var input = el('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png,image/heic,image/heif';
      input.hidden = true;
      input.addEventListener('change', function () {
        onFile(engine, input.files && input.files[0]);
        input.value = '';
      });

      var pick = el('button', 'ttg-photo__pick');
      pick.type = 'button';
      pick.addEventListener('click', function () { input.click(); });

      var tools = el('div', 'ttg-photo__tools');
      tools.hidden = true;

      var zoomWrap = el('label', 'ttg-photo__zoomwrap', S.photo_zoom || 'Zoom');
      var zoom = el('input', 'ttg-photo__zoom');
      zoom.type = 'range';
      // Untergrenze des Reglers wie applyZoom: 1, beim Freischnitt tiefer.
      zoom.min = String(zoomMin(engine)); zoom.max = String(ZOOM_MAX); zoom.step = '0.01';
      zoom.value = String(f.zoom || 1);
      zoom.addEventListener('input', function () { applyZoom(engine, parseFloat(zoom.value) || 1); });
      zoomWrap.appendChild(zoom);

      var auto = el('button', 'ttg-photo__auto', S.photo_auto || 'Automatisch ausrichten');
      auto.type = 'button';
      auto.addEventListener('click', function () {
        f.zoom = 1; zoom.value = '1';
        var p = engine.state.photo;
        if (p) setCrop(engine, coverCrop(p.srcW, p.srcH, frameAspect(engine), 1));
      });

      var hint = el('p', 'ttg-photo__hint', S.photo_hint
        || 'Verschiebe Dein Foto direkt in der Vorschau; zoomen per Regler oder mit zwei Fingern.');

      tools.appendChild(zoomWrap);
      tools.appendChild(auto);
      tools.appendChild(hint);

      var warn = el('p', 'ttg-photo__warn');
      warn.hidden = true;
      var status = el('p', 'ttg-photo__status');
      var retry = el('button', 'ttg-photo__retry', S.photo_retry || 'Upload erneut versuchen');
      retry.type = 'button';
      retry.hidden = true;
      retry.addEventListener('click', function () { upload(engine); });

      mount.appendChild(input);
      mount.appendChild(pick);

      // note / tipps (Schema v1.21) — DIREKT unter dem Upload-Feld, vor den
      // Werkzeugen. Der Hinweis ist ein reines <p>: keine Checkbox, kein
      // „Verstanden", nichts, was den Flow anhalten koennte (_photoSatisfied
      // prueft allein state.photo.url). textContent, damit HTML im Text nicht
      // wirkt; die Umbrueche haelt CSS (white-space:pre-line).
      var cfgP = engine.config.photo || {};
      if (cfgP.note) {
        mount.appendChild(el('p', 'ttg-photo__note', String(cfgP.note)));
      }
      if (cfgP.tipps) {
        // Gemeinsames Popover-Bauteil der Engine. Der Foto-Schritt steht ganz
        // oben in der Karte — deshalb `--unten`: nach oben aufklappend liefe
        // das Panel hier aus dem Bild.
        var tippBox = document.createElement('div');
        tippBox.innerHTML = engine._popoverHtml(
          'foto-tipps',
          String(cfgP.tippsLabel || 'Fototipps'),
          '<p class="ttg-suggest__text"></p>');
        var wrap = tippBox.firstElementChild;
        if (wrap) {
          wrap.classList.add('ttg-suggest--unten');
          // Text per textContent nachtragen — nicht ueber innerHTML, damit der
          // Kundentext nie als Markup interpretiert wird.
          var abs = wrap.querySelector('.ttg-suggest__text');
          if (abs) abs.textContent = String(cfgP.tipps);
          mount.appendChild(wrap);
        }
      }

      mount.appendChild(tools);
      mount.appendChild(warn);
      mount.appendChild(status);
      mount.appendChild(retry);

      f.ui = { pick: pick, tools: tools, zoom: zoom, warn: warn, status: status, retry: retry };
      updateStatus(engine);
      updateWarn(engine);
      bindStage(engine);
    },

    /** Von engine._applyPhotoLayer() bei jedem Preview-Render gerufen (billig). */
    refresh: function (engine) {
      // Crop-Verhaeltnis nachziehen (28.08.2026): Wechselt der Kunde das
      // Design, bekommt die Foto-Ebene per Layout-Override ein ANDERES
      // Seitenverhaeltnis — der crop behielt bisher das alte. Die Anzeige
      // deckt trotzdem (Prozentrechnung), aber verzerrt, und der Druck
      // verzerrt identisch (der Renderer zieht crop auf frame). Heute 1,1 %
      // (Herz 1,2350 gegen Pfote 1,2484) und unsichtbar — die naechste
      // Designachse mit groesserem Unterschied faende den Fehler sonst hier.
      // Re-Cover um die ALTE Mitte mit dem AKTUELLEN Zoom; der Guard bricht
      // die Rekursion setCrop -> renderPreview -> _applyPhotoLayer -> refresh
      // (nach der Korrektur ist die Abweichung 0, der Zweig laeuft nie zweimal).
      var f = fotoState(engine);
      var p = engine.state.photo;
      if (p && p.crop && p.crop.h > 0 && !f._reCover) {
        var soll = frameAspect(engine);
        if (soll > 0 && Math.abs(p.crop.w / p.crop.h - soll) / soll > 0.001) {
          f._reCover = true;
          try {
            setCrop(engine, coverCrop(p.srcW, p.srcH, soll, f.zoom || 1,
              p.crop.x + p.crop.w / 2, p.crop.y + p.crop.h / 2));
          } finally { f._reCover = false; }
        }
      }
      updateWarn(engine);
    },

    /** Nachzügler-Sicherung: Modul lädt NACH einem bereits gerenderten Schritt. */
    mountAll: function () {
      var nodes = document.querySelectorAll('ttg-customizer');
      for (var i = 0; i < nodes.length; i++) {
        var eng = nodes[i].engine;
        if (eng && eng.config && eng.config.photo && typeof eng._mountPhotoStep === 'function') {
          eng._mountPhotoStep();
        }
      }
    },
  };

  window.TTG_FOTO = TTG_FOTO;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', TTG_FOTO.mountAll);
  } else {
    TTG_FOTO.mountAll();
  }
})();
